import type { EmployeeSummary, OutletSummary } from "@kopi/contracts";
import { DEFAULT_EMPLOYEE_SORT, EMPLOYEE_SORTS } from "@kopi/contracts/client";
import { ROLES, type Role } from "@kopi/contracts/codes";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { ConfirmDialog } from "@kopi/ui/components/confirm-button";
import { DataTable, type DataTableColumn, type DataTableRowAction } from "@kopi/ui/components/data-table";
import { Input } from "@kopi/ui/components/input";
import { Label } from "@kopi/ui/components/label";
import { OptionSelect } from "@kopi/ui/components/option-select";
import { ResponsiveDialog } from "@kopi/ui/components/responsive-dialog";
import { toast } from "@kopi/ui/components/sonner";
import { useInfiniteQuery, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { KeyRoundIcon, PlusIcon, ShieldIcon, UserCheckIcon, UserXIcon } from "lucide-react";
import { type FormEvent, useId, useMemo, useState } from "react";
import { Page, PageHeader } from "@/components/page-header";
import { staff } from "@/i18n/messages/staff";
import { type AreaTranslate, useMessages } from "@/i18n/provider";
import type { PlainKeyOf } from "@/i18n/translate";
import { ROLE_KEYS } from "@/lib/access";
import { ApiRequestError, api, unwrap } from "@/lib/api";
import { useConfirmTarget } from "@/lib/confirm-target";
import { nextDraftKey } from "@/lib/draft-key";
import { type StatusFilter, statusOptions, statusToActive } from "@/lib/filters";
import { type EmployeeFilters, employeesKey, employeesQuery, outletsQuery } from "@/lib/queries";
import { sortSearch, urlSorting } from "@/lib/sorting";

/** The column sort lives in the URL (R1-09d), so it survives a reload. */
const validateSearch = sortSearch(EMPLOYEE_SORTS);

export const Route = createFileRoute("/_authed/staff")({
  staticData: { section: "workspace", crumb: "nav.staff" },
  validateSearch,
  loaderDeps: ({ search }) => ({ sort: search.sort }),
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureInfiniteQueryData(employeesQuery({ q: "", sort: deps.sort })),
      context.queryClient.ensureQueryData(outletsQuery),
    ]),
  component: StaffPage,
});

type T = AreaTranslate<typeof staff.id>;
type StaffKey = PlainKeyOf<typeof staff.id>;

/** Which dialog is open, and for whom. */
type Dialog = { kind: "create" } | { kind: "roles" | "pin"; employee: EmployeeSummary } | null;

function StaffPage() {
  const { t } = useMessages(staff);
  const { data: outlets } = useSuspenseQuery(outletsQuery);
  const queryClient = useQueryClient();
  const { sort } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [dialog, setDialog] = useState<Dialog>(null);
  const deactivate = useConfirmTarget<EmployeeSummary>();
  const filters: EmployeeFilters = { q, active: statusToActive(status), sort };
  const sorting = urlSorting(EMPLOYEE_SORTS, DEFAULT_EMPLOYEE_SORT, sort, (next) => {
    void navigate({ search: next ? { sort: next } : {}, replace: true });
  });
  const employees = useInfiniteQuery(employeesQuery(filters));
  const close = () => setDialog(null);

  const toggle = useMutation({
    mutationFn: async (employee: EmployeeSummary) =>
      unwrap(await api.v1.employees({ employeeId: employee.id }).patch({ active: !employee.active })),
    onSuccess: async (_, employee) => {
      await queryClient.invalidateQueries({ queryKey: employeesKey });
      toast.success(
        t(employee.active ? "staff.deactivated" : "staff.reactivated", { name: employee.displayName }),
      );
    },
    onError: (error) => toast.error(errorMessage(t, error, "toggle")),
  });

  const columns = useMemo(() => staffColumns(t, outlets), [t, outlets]);
  const rowActions = (employee: EmployeeSummary): DataTableRowAction[] => [
    {
      label: t("staff.editRoles"),
      icon: <ShieldIcon />,
      onSelect: () => setDialog({ kind: "roles", employee }),
    },
    {
      label: t("staff.setPin"),
      icon: <KeyRoundIcon />,
      onSelect: () => setDialog({ kind: "pin", employee }),
    },
    employee.active
      ? {
          label: t("common.deactivate"),
          icon: <UserXIcon />,
          destructive: true,
          disabled: toggle.isPending,
          onSelect: () => deactivate.ask(employee),
        }
      : {
          label: t("common.activate"),
          icon: <UserCheckIcon />,
          disabled: toggle.isPending,
          onSelect: () => toggle.mutate(employee),
        },
  ];

  const addButton = (
    <Button type="button" onClick={() => setDialog({ kind: "create" })}>
      <PlusIcon />
      {t("staff.add")}
    </Button>
  );

  return (
    <Page>
      <PageHeader title={t("nav.staff")} subtitle={t("staff.subtitle")} actions={addButton} />
      <DataTable
        paging="pages"
        label={t("staff.list")}
        columns={columns}
        query={employees}
        getRowId={(employee) => employee.id}
        search={{ value: q, onChange: setQ, placeholder: t("staff.search") }}
        filters={
          <OptionSelect
            aria-label={t("common.status")}
            className="md:w-44"
            options={statusOptions(t)}
            value={status}
            onValueChange={setStatus}
          />
        }
        {...sorting}
        rowActions={rowActions}
        empty={{
          title: t(status === "all" ? "staff.emptyTitle" : "staff.emptyFilteredTitle"),
          description: t("staff.emptyHint"),
        }}
      />
      {dialog?.kind === "create" && <AddEmployeeDialog outlets={outlets} onClose={close} />}
      {dialog?.kind === "roles" && (
        <RolesDialog employee={dialog.employee} outlets={outlets} onClose={close} />
      )}
      {dialog?.kind === "pin" && <PinDialog employee={dialog.employee} onClose={close} />}
      <ConfirmDialog
        open={deactivate.open}
        onOpenChange={deactivate.onOpenChange}
        title={t("staff.deactivateTitle", { name: deactivate.target?.displayName ?? "" })}
        description={t("staff.deactivateHint")}
        confirmLabel={t("common.deactivate")}
        onConfirm={() => deactivate.target && toggle.mutate(deactivate.target)}
      />
    </Page>
  );
}

function outletName(t: T, outlets: OutletSummary[], outletId: string | null): string {
  return outletId === null
    ? t("staff.allOutlets")
    : (outlets.find((o) => o.id === outletId)?.name ?? t("staff.otherOutlet"));
}

function staffColumns(t: T, outlets: OutletSummary[]): DataTableColumn<EmployeeSummary>[] {
  return [
    {
      id: "name",
      header: t("common.name"),
      enableSorting: true,
      meta: { card: "title" },
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-medium">{row.original.displayName}</span>
          <span className="truncate font-normal text-muted-foreground text-sm">
            {row.original.email ?? t("staff.noWebAccount")}
          </span>
        </div>
      ),
    },
    {
      id: "roles",
      header: t("staff.role"),
      cell: ({ row }) => (
        <ul className="flex flex-wrap justify-end gap-1 md:justify-start">
          {row.original.roles.map((r) => (
            <li key={`${r.role}:${r.outletId}`}>
              <Badge variant="outline">
                {t(ROLE_KEYS[r.role])} · {outletName(t, outlets, r.outletId)}
              </Badge>
            </li>
          ))}
        </ul>
      ),
    },
    {
      id: "status",
      header: t("common.status"),
      enableSorting: true,
      meta: { className: "md:w-32" },
      cell: ({ row }) =>
        row.original.active ? (
          <Badge variant="success">{t("common.active")}</Badge>
        ) : (
          <Badge variant="outline">{t("common.inactive")}</Badge>
        ),
    },
  ];
}

/** Role + outlet picker. Owner is tenant-wide, so the outlet field disappears for it. */
function RolePicker(props: {
  outlets: OutletSummary[];
  role: Role;
  outletId: string;
  onChange: (role: Role, outletId: string) => void;
}) {
  const { t } = useMessages(staff);
  const roleId = useId();
  const outletSelectId = useId();
  const roleOptions = ROLES.map((role) => ({ value: role, label: t(ROLE_KEYS[role]) }));
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={roleId}>{t("staff.role")}</Label>
        <OptionSelect
          id={roleId}
          options={roleOptions}
          value={props.role}
          onValueChange={(role) => props.onChange(role, props.outletId)}
        />
      </div>
      {props.role !== "owner" && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={outletSelectId}>{t("outlet.heading")}</Label>
          {/* A tenant's outlets are a small, bounded set (CODING_STANDARDS §5: OptionSelect is fine). */}
          <OptionSelect
            id={outletSelectId}
            options={props.outlets.map((outlet) => ({ value: outlet.id, label: outlet.name }))}
            value={props.outletId}
            onValueChange={(outletId) => props.onChange(props.role, outletId)}
          />
        </div>
      )}
    </div>
  );
}

type StaffAction = "create" | "roles" | "toggle" | "pin";

/** What to fix when the API rejects the input, per screen action. */
const VALIDATION_HINTS = {
  create: "staff.createInvalid",
  roles: "staff.rolesInvalid",
  toggle: "staff.toggleInvalid",
  pin: "staff.pinInvalid",
} as const satisfies Record<StaffAction, StaffKey>;

/** Action-specific messages; API `message` texts (English) are never shown to staff. */
function errorMessage(t: T, error: unknown, action: StaffAction): string {
  if (!(error instanceof ApiRequestError)) return t("common.saveOffline");
  switch (error.code) {
    case "email_unavailable":
      return t("staff.emailTaken");
    case "cannot_change_self":
      return t("staff.cannotChangeSelf");
    case "invalid_outlet":
      return t("staff.invalidOutlet");
    case "invalid_role_scope":
      return t("staff.invalidRoleScope");
    case "validation_failed":
      return t(VALIDATION_HINTS[action]);
    case "not_found":
      return t("staff.notFound");
    case "forbidden":
      return t("staff.forbidden");
    default:
      return t("common.saveFailed");
  }
}

function FieldError(props: { error: unknown; action: StaffAction }) {
  const { t } = useMessages(staff);
  if (!props.error) return null;
  return (
    <p role="alert" className="text-destructive text-sm">
      {errorMessage(t, props.error, props.action)}
    </p>
  );
}

/** Mounted only while open, so every opening starts from a blank form. */
function AddEmployeeDialog(props: { outlets: OutletSummary[]; onClose: () => void }) {
  const { t } = useMessages(staff);
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("cashier");
  const [outletId, setOutletId] = useState(props.outlets[0]?.id ?? "");
  const nameId = useId();
  const emailId = useId();
  const passwordId = useId();

  const create = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.v1.employees.post({
          displayName,
          roles: [{ role, outletId: role === "owner" ? null : outletId }],
          ...(email ? { account: { email, password } } : {}),
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: employeesKey });
      toast.success(t("staff.added", { name: displayName.trim() }));
      props.onClose();
    },
  });
  const incomplete = !displayName.trim() || (!!email && password.length < 12);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!create.isPending && !incomplete) create.mutate();
  }

  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("staff.add")}
      description={t("staff.addHint")}
      dismissible={!create.isPending}
      footer={
        <Button type="submit" form="add-employee" disabled={create.isPending || incomplete}>
          {t(create.isPending ? "common.saving" : "common.save")}
        </Button>
      }
    >
      <form id="add-employee" className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={nameId}>{t("common.name")}</Label>
          <Input id={nameId} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <RolePicker
          outlets={props.outlets}
          role={role}
          outletId={outletId}
          onChange={(r, o) => {
            setRole(r);
            setOutletId(o);
          }}
        />
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={emailId}>{t("staff.email")}</Label>
          <Input
            id={emailId}
            type="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        {email && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={passwordId}>{t("staff.password")}</Label>
            <Input
              id={passwordId}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        )}
        <FieldError error={create.error} action="create" />
      </form>
    </ResponsiveDialog>
  );
}

interface RoleRow {
  key: string;
  role: Role;
  outletId: string;
}

function newRow(role: Role, outletId: string): RoleRow {
  return { key: `row-${nextDraftKey()}`, role, outletId };
}

/** Edits the employee's full list of role bindings; saving replaces them all (PUT …/roles). */
function RolesDialog(props: { employee: EmployeeSummary; outlets: OutletSummary[]; onClose: () => void }) {
  const { employee, outlets } = props;
  const { t } = useMessages(staff);
  const queryClient = useQueryClient();
  const defaultOutlet = outlets[0]?.id ?? "";
  const [rows, setRows] = useState<RoleRow[]>(() =>
    employee.roles.map((r) => newRow(r.role, r.outletId ?? defaultOutlet)),
  );

  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.v1.employees({ employeeId: employee.id }).roles.put({
          roles: rows.map((r) => ({ role: r.role, outletId: r.role === "owner" ? null : r.outletId })),
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: employeesKey });
      toast.success(t("staff.rolesSaved", { name: employee.displayName }));
      props.onClose();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!save.isPending && rows.length > 0) save.mutate();
  }

  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("staff.rolesTitle", { name: employee.displayName })}
      description={t("staff.rolesHint")}
      dismissible={!save.isPending}
      className="md:max-w-xl"
      footer={
        <Button type="submit" form="employee-roles" disabled={save.isPending || rows.length === 0}>
          {t(save.isPending ? "common.saving" : "staff.saveRoles")}
        </Button>
      }
    >
      <form id="employee-roles" className="flex flex-col gap-3" onSubmit={submit} noValidate>
        {rows.map((row) => (
          <div key={row.key} className="flex flex-col gap-2 rounded-lg border p-3">
            <RolePicker
              outlets={outlets}
              role={row.role}
              outletId={row.outletId}
              onChange={(role, outletId) =>
                setRows((current) => current.map((r) => (r.key === row.key ? { ...r, role, outletId } : r)))
              }
            />
            {rows.length > 1 && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="self-start"
                onClick={() => setRows((current) => current.filter((r) => r.key !== row.key))}
              >
                {t("staff.removeRole")}
              </Button>
            )}
          </div>
        ))}
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="self-start"
          onClick={() => setRows((current) => [...current, newRow("cashier", defaultOutlet)])}
        >
          <PlusIcon />
          {t("staff.addRole")}
        </Button>
        <FieldError error={save.error} action="roles" />
      </form>
    </ResponsiveDialog>
  );
}

/** Sets or resets the employee's register PIN; the manager hands it over in person. */
function PinDialog(props: { employee: EmployeeSummary; onClose: () => void }) {
  const { t } = useMessages(staff);
  const [pin, setPin] = useState("");
  const pinId = useId();
  const save = useMutation({
    mutationFn: async () =>
      unwrap(await api.v1.employees({ employeeId: props.employee.id }).pin.put({ pin })),
    onSuccess: () => {
      toast.success(t("staff.pinSaved"));
      props.onClose();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!save.isPending && pin.length >= 4) save.mutate();
  }

  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("staff.pinTitle", { name: props.employee.displayName })}
      description={t("staff.pinHint")}
      dismissible={!save.isPending}
      footer={
        <Button type="submit" form="employee-pin" disabled={save.isPending || pin.length < 4}>
          {t(save.isPending ? "common.saving" : "staff.savePin")}
        </Button>
      }
    >
      <form id="employee-pin" className="flex flex-col gap-3" onSubmit={submit} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={pinId}>{t("staff.newPin")}</Label>
          <Input
            id={pinId}
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            maxLength={6}
            className="font-mono tracking-widest"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          />
        </div>
        <FieldError error={save.error} action="pin" />
      </form>
    </ResponsiveDialog>
  );
}
